import { Menu } from '@headlessui/react';
import Avatar from '@sendsprint/ui-react/dist/components/Avatar';
import Box from '@sendsprint/ui-react/dist/components/Box';
import Icon from '@sendsprint/ui-react/dist/components/Icon';
import Text from '@sendsprint/ui-react/dist/components/Text';
import ClientApi from '@src/types/client';
import { getInitials } from '@src/utils/user';
import React, { lazy, useMemo, useState } from 'react';
import cs from 'classnames';
import { Path } from '@src/navigations/routes';
import { LoggedInClient } from '../../..';
import SuspenseWrapper from '@src/components/suspenseWrapper';
import Skeleton from '@sendsprint/ui-react/dist/components/Skeleton';
import lazyWithPreload from '@src/utils/lazyWithPreload';
import FaSpinner from '@src/assets/svgs/transformed/react-icons/fa/FaSpinner';

const ArrowIosDownwardOutline = lazy(
  () => import('@sendsprint/ui-react/dist/icons/ArrowIosDownwardOutline')
);
const PersonOutline = lazy(() => import('@sendsprint/ui-react/dist/icons/PersonOutline'));

const DropdownTransition = lazyWithPreload(
  () =>
    import(
      /*webpackChunkName: 'headerDropdownTransition'*/ /* webpackPrefetch: true */ './DropdownTransition'
    )
);

interface Props {
  user: ClientApi.General.UserProfile | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoutHandler: () => Promise<any>;
  client?: LoggedInClient;
  logoutLoading: boolean;
}

const Dropdown = ({ user, logoutHandler, client, logoutLoading }: Props) => {
  const [isHovered, setIsHovered] = useState(false);

  const dropdownData = useMemo(() => {
    if (client === 'receiver')
      return [{ icon: PersonOutline, label: 'Manage Account', linkPath: Path.ReceiverSettings }];
    if (client === 'white-label')
      return [{ icon: PersonOutline, label: 'Manage Account', linkPath: Path.WhiteLabelSettings }];
    return [{ icon: PersonOutline, label: 'Manage Account', linkPath: Path.SenderSettings }];
  }, []);

  // const shownData = isMobile ? dropdownData2 : dropdownData;

  const initials = getInitials(user?.fullName || '');

  const handleDropdownPreload = () => {
    setIsHovered(true);
    DropdownTransition.preload();
  };

  return (
    <Menu as={'div'} className="ss-relative ss-block">
      {({ open }) => (
        <>
          <Menu.Button
            disabled={logoutLoading}
            onMouseEnter={handleDropdownPreload}
            onMouseLeave={() => setIsHovered(false)}
            className={cs(
              'ss-flex ss-items-center ss-duration-200 ss-gap-2 ss-p-2 ss-rounded-full focus:ss-focus-ring',
              {
                'ss-bg-success-100': isHovered,
                'ss-opacity-50 ss-cursor-not-allowed': logoutLoading
              }
            )}>
            <Box className="ss-relative">
              <Avatar
                className="ss-w-8 ss-h-8 ss-flex ss-items-center"
                imageSrc={user?.profilePixUrl}
                size={'100%'}
                initials={initials}
              />
            </Box>
            <Text className="ss-sr-only md:ss-not-sr-only ss-hidden lg:ss-inline-block">
              {user?.firstName}
            </Text>
            {logoutLoading && <FaSpinner className="ss-animate-spin" />}
            <SuspenseWrapper
              suspenseFallback={<Skeleton className="ss-w-7 ss-h-3 ss-rounded" />}
              returnNullErrorFallback>
              <Icon
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                svg={ArrowIosDownwardOutline as any}
                size={28}
                className={cs(
                  'ss-text-primary-100 lg:ss-inline-block ss-transition ss-transform-gpu',
                  {
                    'ss-duration-100 ss-rotate-180': open,
                    'ss-duration-75 ss-rotate-0': !open
                  }
                )}
              />
            </SuspenseWrapper>
          </Menu.Button>
          {open && (
            <SuspenseWrapper returnNullErrorFallback>
              <DropdownTransition
                logoutHandler={logoutHandler}
                open={open}
                shownData={dropdownData}
              />
            </SuspenseWrapper>
          )}
        </>
      )}
    </Menu>
  );
};

export default Dropdown;
